import { useEffect, useState } from 'react';
import { supabase } from '../../lib/supabase';
import { usePricing, priceFor, periodFor } from '../../hooks/usePricing';
import { Loader2, CreditCard, Crown, Users, ExternalLink, Zap, Link2, CheckCircle2, Clock, Sparkles, HelpCircle, AlertTriangle } from 'lucide-react';
import { SubscriptionCheckoutModal } from './SubscriptionCheckoutModal';
import { RefundRequestModal } from './RefundRequestModal';
import { tierDisplayName } from '../../lib/tiers';
import { errorMessage, reportError } from '../../lib/errors';
import toast from 'react-hot-toast';
import { useConfirm } from '../../components/ui/ConfirmProvider';

interface SubInfo {
    subscription_tier: string;
    max_members: number;
    member_count: number;
    pending_invites: number;
    extra_seats: number;
    stripe_account_id: string | null;
    // 'express' is an account ForgeDash created and runs on the trader's behalf;
    // 'standard' is a legacy OAuth connection to the trader's own Stripe account,
    // grandfathered in. The two differ in where the trader manages payouts, so
    // the panel below branches on it.
    stripe_account_type: 'express' | 'standard' | null;
    // Written by the Connect webhook (stripe-connect-webhook). Connecting an
    // account is not the same as being able to take money or receive it, and the
    // panel used to claim "ready to accept payments" the moment an account id
    // existed — including for an account Stripe was still verifying.
    stripe_charges_enabled: boolean;
    stripe_payouts_enabled: boolean;
    // Onboarding form submitted, which is NOT the same as verified. Someone who
    // has finished has nothing to do but wait; someone who bailed half way needs
    // the form re-opened, and both look identical through charges_enabled alone.
    stripe_details_submitted: boolean;
    stripe_onboarding_complete: boolean;
    stripe_requirements_needed: string[];
    orgId: string | null;
    cancel_at_period_end: boolean;
    current_period_end: string | null;
    user_email: string;
    user_full_name: string;
}

// The 3 paid tiers. Names / prices / features mirror the marketing
// /pricing page (Pro / Auto / Full Auto).
//
// `id` matches organizations.subscription_tier and must not change without a
// data migration. It is also the ONLY billing identifier the client sends —
// checkout receives the tier key and resolves the Stripe price server-side
// (E-04).
//
// Audit S-01 (2026-08-06): `priceId` and `productId` used to live here as
// hardcoded TEST-mode Stripe ids. They had already been dead since E-04 made
// the client send `tier.id`, but they made the dashboard a third place that
// would need editing at the live cutover — and Stripe price objects do not
// exist across modes. Removed; the mapping lives in the STRIPE_PRICE_* secrets
// and supabase/functions/_shared/stripe.ts.
//
// Displayed `price` is now a FALLBACK ONLY. The figure actually rendered comes
// from Stripe via usePricing() -> the get-pricing edge function, so it cannot
// drift from what the customer is charged. These strings show during the fetch
// and if it fails, so the card never renders with a hole where a price goes.
const TIERS = [
    {
        id: 'pro',
        name: 'Pro',
        price: '£39.99',
        period: '/mo',
        blurb: 'No limits. Get paid before your customer leaves the yard.',
        features: [
            'Unlimited jobs & invoices',
            'Bank details on invoices',
            'One extra seat',
            'Priority support',
        ],
        inherits: 'Everything in Free',
        active: true,
    },
    {
        id: 'auto',
        name: 'Auto',
        price: '£79.99',
        period: '/mo',
        blurb: 'Books jobs while you work. Chases invoices while you sleep.',
        features: [
            'AI Receptionist — answers and books jobs while you work',
            'Automated invoice chasing',
            'Smart scheduling',
            'Advanced reporting',
            'Three extra seats',
        ],
        inherits: 'Everything in Pro',
        active: false, // Coming soon
    },
    {
        id: 'full_auto',
        name: 'Full Auto',
        price: '£249.99',
        period: '/mo',
        blurb: 'Your garage admin runs itself. Almost.',
        features: [
            'Fully autonomous admin workflows',
            'Self Assessment — review and confirm',
            'Dedicated account manager',
            'Five extra seats',
            'Effectively replaces a bookkeeper',
        ],
        inherits: 'Everything in Auto',
        active: false, // Coming soon
    }
];

export const SubscriptionSection = () => {
    const { prices } = usePricing();
    const [loading, setLoading] = useState(true);
    const confirm = useConfirm();
    const [sub, setSub] = useState<SubInfo>({
        subscription_tier: 'free',
        max_members: 1,
        member_count: 0,
        pending_invites: 0,
        extra_seats: 0,
        stripe_account_id: null,
        stripe_account_type: null,
        stripe_charges_enabled: false,
        stripe_payouts_enabled: false,
        stripe_details_submitted: false,
        stripe_onboarding_complete: false,
        stripe_requirements_needed: [],
        orgId: null,
        cancel_at_period_end: false,
        current_period_end: null,
        user_email: '',
        user_full_name: ''
    });
    const [connectLoading, setConnectLoading] = useState(false);
    const [dashboardLoading, setDashboardLoading] = useState(false);
    const [disconnectLoading, setDisconnectLoading] = useState(false);
    const [manageLoading, setManageLoading] = useState(false);

    const [checkoutModalOpen, setCheckoutModalOpen] = useState(false);
    const [checkoutPriceId, setCheckoutPriceId] = useState<string | null>(null);
    const [refundModalOpen, setRefundModalOpen] = useState(false);

    useEffect(() => {
        const fetchSub = async () => {
            const { data: { user } } = await supabase.auth.getUser();
            if (!user) return;

            const { data: profile, error: profileError } = await supabase
                .from('profiles')
                .select('active_org_id, full_name')
                .eq('id', user.id)
                .single();
            if (profileError) reportError(profileError, 'subscription-section.profiles');

            if (!profile?.active_org_id) return;

            const { data: org, error: orgError } = await supabase
                .from('organizations')
                .select('subscription_tier, max_members, extra_seats, stripe_account_id, stripe_account_type, cancel_at_period_end, current_period_end, stripe_charges_enabled, stripe_payouts_enabled, stripe_details_submitted, stripe_onboarding_complete, stripe_requirements_needed')
                .eq('id', profile.active_org_id)
                .single();
            if (orgError) reportError(orgError, 'subscription-section.organizations');

            const { count } = await supabase
                .from('organization_members')
                .select('id', { count: 'exact', head: true })
                .eq('org_id', profile.active_org_id);

            // Seats the database enforces (plan allowance + extra seats bought
            // through support) and invites still holding one (20261013220000).
            const { data: seatLimit, error: seatLimitError } = await supabase
                .rpc('fn_org_seat_limit', { p_org_id: profile.active_org_id });
            if (seatLimitError) reportError(seatLimitError, 'subscription-section.fn_org_seat_limit');
            const { count: pendingInvites } = await supabase
                .from('organization_invites')
                .select('id', { count: 'exact', head: true })
                .eq('org_id', profile.active_org_id)
                .eq('status', 'pending')
                .gt('expires_at', new Date().toISOString());

            setSub({
                subscription_tier: org?.subscription_tier || 'free',
                max_members: typeof seatLimit === 'number' ? seatLimit : (org?.max_members || 1),
                member_count: count || 0,
                pending_invites: pendingInvites || 0,
                extra_seats: org?.extra_seats || 0,
                stripe_account_id: org?.stripe_account_id || null,
                stripe_account_type: (org?.stripe_account_type as SubInfo['stripe_account_type']) || null,
                stripe_charges_enabled: !!org?.stripe_charges_enabled,
                stripe_payouts_enabled: !!org?.stripe_payouts_enabled,
                stripe_details_submitted: !!org?.stripe_details_submitted,
                stripe_onboarding_complete: !!org?.stripe_onboarding_complete,
                stripe_requirements_needed: (org?.stripe_requirements_needed as string[]) || [],
                orgId: profile.active_org_id,
                cancel_at_period_end: org?.cancel_at_period_end || false,
                current_period_end: org?.current_period_end || null,
                user_email: user.email || '',
                user_full_name: profile.full_name || ''
            });
            setLoading(false);
        };

        fetchSub();
    }, []);

    // Express onboarding. There is no Stripe signup for the trader to do first —
    // ForgeDash creates the account server-side on the first call and hands back
    // a link into Stripe's hosted form, which is also what "finish setup"
    // reopens. Account Links expire in minutes, so one is minted per click and
    // never cached.
    const handleStripeConnect = async () => {
        if (!sub.orgId) return;
        setConnectLoading(true);

        try {
            const { data, error } = await supabase.functions.invoke('stripe-connect/onboard', {
                body: { org_id: sub.orgId }
            });

            if (error) throw error;
            if (data?.error) throw new Error(data.error);

            if (data?.url) {
                window.location.href = data.url;
            } else {
                throw new Error('Failed to open Stripe setup');
            }
        } catch (err) {
            console.error('Stripe onboarding error:', err);
            toast.error('Could not open payment setup: ' + errorMessage(err));
        } finally {
            setConnectLoading(false);
        }
    };

    // Express dashboard login link. Valid for about a minute and single-use, so
    // it is fetched on the click rather than rendered into an href — and opened
    // in the same tab, because a popup blocker eating it would look like a dead
    // button.
    const handleOpenPayoutsDashboard = async () => {
        if (!sub.orgId) return;
        setDashboardLoading(true);

        try {
            const { data, error } = await supabase.functions.invoke('stripe-connect/dashboard-link', {
                body: { org_id: sub.orgId }
            });

            if (error) throw error;
            if (data?.error) throw new Error(data.error);
            if (!data?.url) throw new Error('Could not open your payments dashboard');

            window.location.href = data.url;
        } catch (err) {
            console.error('Stripe dashboard link error:', err);
            toast.error(errorMessage(err) || 'Could not open your payments dashboard');
        } finally {
            setDashboardLoading(false);
        }
    };

    const handleDisconnectStripe = async () => {
        if (!sub.orgId || !await confirm({ title: 'Disconnect Stripe?', message: 'You will not be able to accept card payments until you reconnect.', confirmText: 'Disconnect', isDangerous: true })) return;
        setDisconnectLoading(true);

        try {
            const { data, error } = await supabase.functions.invoke('stripe-connect/disconnect', {
                body: { org_id: sub.orgId }
            });

            if (error) throw error;
            if (data?.error) throw new Error(data.error);

            setSub(prev => ({
                ...prev,
                stripe_account_id: null,
                stripe_account_type: null,
                stripe_charges_enabled: false,
                stripe_payouts_enabled: false,
                stripe_details_submitted: false,
                stripe_onboarding_complete: false,
                stripe_requirements_needed: [],
            }));
            toast.success("Stripe disconnected successfully.");
        } catch (err) {
            console.error('Stripe disconnect error:', err);
            toast.error('Failed to disconnect Stripe: ' + errorMessage(err));
        } finally {
            setDisconnectLoading(false);
        }
    };

    const handleManageSubscription = async () => {
        if (!sub.orgId) return;
        if (sub.subscription_tier === 'free') {
            toast.error("You must activate a premium plan to access the billing portal.");
            return;
        }

        // Founder decision 2026-10-13: a downgrade to Free removes everyone but
        // the owner, revokes pending invites and drops extra seats
        // (fn_remove_team_on_free). Cancelling happens in Stripe's portal, so
        // say it here, before the owner leaves the app.
        const team = sub.member_count - 1;
        // Extra seats count even while unused: support billed for them.
        if (team > 0 || sub.pending_invites > 0 || sub.extra_seats > 0) {
            const lines = [
                team > 0 && `${team} team member${team === 1 ? '' : 's'} will be removed and lose access to this workshop.`,
                sub.pending_invites > 0 && `${sub.pending_invites} pending invite${sub.pending_invites === 1 ? '' : 's'} will be cancelled.`,
                sub.extra_seats > 0 && `Your ${sub.extra_seats} extra seat${sub.extra_seats === 1 ? '' : 's'} bought through support will be removed.`,
            ].filter(Boolean).join(' ');
            const ok = await confirm({
                title: 'Cancelling or downgrading removes your team and extra seats',
                message: `If you cancel or move to the Free plan, then when your current period ends: ${lines} Your own account, jobs and records are not affected. You can invite people again after upgrading.`,
                confirmText: 'I understand — continue',
                isDangerous: true,
            });
            if (!ok) return;
        }

        setManageLoading(true);
        try {
            const { data, error } = await supabase.functions.invoke('manage-subscription', {
                body: { action: 'create_portal', org_id: sub.orgId }
            });

            if (error) throw error;
            if (data?.error) throw new Error(data.error);
            if (data?.url) {
                window.location.href = data.url;
            } else {
                throw new Error('Failed to generate portal link');
            }
        } catch (err) {
            console.error('Failed to open billing portal:', err);
            toast.error(errorMessage(err) || 'Unable to load billing portal.');
        } finally {
            setManageLoading(false);
        }
    };

    if (loading) return <div className="p-8 flex justify-center"><Loader2 className="animate-spin text-fg-4" /></div>;

    return (
        <div className="space-y-8 animate-in fade-in slide-in-from-bottom-2 duration-300">
            <div>
                <h3 className="text-xl font-bold text-fg flex items-center gap-2">
                    <CreditCard className="w-5 h-5 text-accent" />
                    Subscription & Billing
                </h3>
                <p className="text-sm text-fg-2">Your current plan, Stripe connection, and billing</p>
            </div>

            {/* Payments (Stripe Express).
                Connected, able to charge, and able to receive payouts are three
                different things — this used to collapse them into "connected and
                ready to accept payments" as soon as an account id existed.

                The trader does not have a Stripe account of their own to manage:
                ForgeDash creates and runs an Express account for them, so the
                word "Stripe" barely appears here on purpose. Legacy Standard
                connections (stripe_account_type === 'standard') are the exception
                and still point at the trader's own Stripe dashboard. */}
            {(() => {
                const isLegacyStandard = sub.stripe_account_type === 'standard';
                const ready = !!sub.stripe_account_id && sub.stripe_charges_enabled;
                // Submitted but not yet chargeable: nothing for them to do but wait.
                const awaitingReview = !!sub.stripe_account_id && !sub.stripe_charges_enabled && sub.stripe_details_submitted;
                // Started and abandoned, or never started: the form needs reopening.
                const needsFinishing = !!sub.stripe_account_id && !sub.stripe_details_submitted;

                return (
            <div className={`p-4 rounded-[var(--fds-radius)] border space-y-3 ${ready
                ? 'bg-success/5 border-success/20'
                : 'bg-gold-fill/5 border-gold/20'
                }`}>
                <div className="flex flex-wrap items-center justify-between gap-3">
                    <div className="flex items-center gap-3 min-w-0">
                        <div className={`w-10 h-10 shrink-0 rounded-full flex items-center justify-center ${ready ? 'bg-success/15' : 'bg-gold-fill/15'
                            }`}>
                            {ready
                                ? <CheckCircle2 className="w-5 h-5 text-success" />
                                : awaitingReview
                                    ? <Clock className="w-5 h-5 text-gold" />
                                    : <Link2 className="w-5 h-5 text-gold" />
                            }
                        </div>
                        <div className="min-w-0">
                            <p className="text-sm font-medium text-fg">
                                {!sub.stripe_account_id
                                    ? 'Set up card payments'
                                    : ready
                                        ? 'Card payments are on'
                                        : awaitingReview
                                            ? 'Details received — being checked'
                                            : 'Finish setting up payments'}
                            </p>
                            <p className="text-xs text-fg-2">
                                {!sub.stripe_account_id
                                    ? 'Takes about two minutes. Needed before customers can pay an invoice by card.'
                                    : ready
                                        ? 'Customers can pay your invoices by card, and the money goes to your bank.'
                                        : awaitingReview
                                            ? "Nothing more to do — we'll tell you the moment you can take payments."
                                            : 'A few details are still missing before you can take card payments.'}
                            </p>
                        </div>
                    </div>
                    <div className="flex flex-wrap items-center gap-2">
                        {(!sub.stripe_account_id || needsFinishing) && (
                            <button
                                onClick={handleStripeConnect}
                                disabled={connectLoading}
                                className="flex items-center gap-2 px-4 py-2 min-h-[44px] fd-btn-primary rounded-[var(--fds-radius)] text-sm font-semibold transition-opacity disabled:opacity-50 whitespace-nowrap"
                            >
                                {connectLoading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Zap className="w-4 h-4" />}
                                {sub.stripe_account_id ? 'Finish setup' : 'Set up payments'}
                            </button>
                        )}
                        {sub.stripe_account_id && !isLegacyStandard && (
                            /* Express dashboard: payments, payouts and customers,
                               and nothing else. No password — the link is minted
                               from this session and dies in about a minute, which
                               is why it is a button and not an href. */
                            <button
                                onClick={handleOpenPayoutsDashboard}
                                disabled={dashboardLoading}
                                className="flex items-center gap-2 px-4 py-2 min-h-[44px] bg-raised text-fg border border-line rounded-[var(--fds-radius)] text-sm font-medium hover:opacity-90 transition-opacity disabled:opacity-50 whitespace-nowrap"
                            >
                                {dashboardLoading ? <Loader2 className="w-4 h-4 animate-spin" /> : <ExternalLink className="w-4 h-4" />}
                                Payments &amp; payouts
                            </button>
                        )}
                        {sub.stripe_account_id && isLegacyStandard && (
                            /* Grandfathered OAuth connection to the trader's OWN
                               Stripe account. There is no Express dashboard to
                               link to and no login link we can mint for them. */
                            <a
                                href="https://dashboard.stripe.com/"
                                target="_blank"
                                rel="noopener noreferrer"
                                className="flex items-center gap-2 px-4 py-2 min-h-[44px] bg-raised text-fg border border-line rounded-[var(--fds-radius)] text-sm font-medium hover:opacity-90 transition-opacity whitespace-nowrap"
                            >
                                <ExternalLink className="w-4 h-4" />
                                Manage on Stripe
                            </a>
                        )}
                        {sub.stripe_account_id && (
                            <button
                                onClick={handleDisconnectStripe}
                                disabled={disconnectLoading}
                                className="flex items-center gap-2 px-4 py-2 min-h-[44px] bg-danger/10 text-danger rounded-[var(--fds-radius)] border border-danger/20 text-sm font-medium hover:bg-danger/20 transition-opacity disabled:opacity-50 whitespace-nowrap"
                            >
                                {disconnectLoading ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Turn off'}
                            </button>
                        )}
                    </div>
                </div>

                {sub.stripe_account_id && (
                    <div className="text-xs text-fg-2 border-t border-line pt-3 space-y-1.5">
                        {/* Asked for after a live test payment: money left the payer's
                            account immediately but showed as "available 24/08" for a
                            payment taken on 17/08, with nothing in the app explaining
                            the gap. */}
                        <p>
                            <span className="font-medium text-fg">When you get paid:</span>{' '}
                            card payments go to your own payments balance, not to ForgeDash.
                            Stripe holds new funds for a few days before they become available
                            {sub.stripe_payouts_enabled
                                ? ' and then pays out to your bank automatically.'
                                : '. Payouts are not switched on yet — add your bank details to turn them on.'}
                            {isLegacyStandard
                                ? ' Your exact payout dates are on your Stripe dashboard.'
                                : ' Payment and payout dates are under Payments & payouts.'}
                        </p>
                        {sub.stripe_requirements_needed.length > 0 && (
                            <p className="text-gold">
                                {sub.stripe_requirements_needed.length} detail
                                {sub.stripe_requirements_needed.length === 1 ? ' is' : 's are'} still needed before your
                                account is fully verified.{' '}
                                {isLegacyStandard ? 'Open Manage on Stripe to finish.' : 'Use Finish setup to complete them.'}
                            </p>
                        )}
                    </div>
                )}
            </div>
                );
            })()}

            {/* Current Plan Summary */}
            <div className="p-5 bg-raised border border-line rounded-[var(--fds-radius)] space-y-4">
                <div className="flex items-start justify-between">
                    <div className="space-y-1">
                        <p className="text-xs font-medium text-fg-2 uppercase tracking-wider">Current Plan</p>
                        <div className="flex items-center gap-3">
                            <Crown className="w-6 h-6 text-accent" />
                            <span className="text-lg font-bold text-fg">
                                {sub.cancel_at_period_end && sub.subscription_tier !== 'free' ? 'Free' : tierDisplayName(sub.subscription_tier)}
                            </span>
                            {sub.cancel_at_period_end && sub.subscription_tier !== 'free' && sub.current_period_end && (
                                <span className="text-xs font-medium text-gold bg-gold-fill/10 px-2.5 py-1 rounded-md border border-gold/20 flex items-center gap-1.5 ml-2">
                                    <Clock className="w-3.5 h-3.5" />
                                    {tierDisplayName(sub.subscription_tier)} active until {new Date(sub.current_period_end).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' })}
                                </span>
                            )}
                        </div>
                    </div>
                    <div className="flex flex-col gap-2">
                        <button
                            onClick={handleManageSubscription}
                            disabled={manageLoading}
                            className="flex items-center gap-2 px-3 py-1.5 border border-line rounded-[var(--fds-radius)] text-xs font-medium text-fg-2 hover:bg-raised transition-colors disabled:opacity-50"
                        >
                            {manageLoading ? <Loader2 className="w-3 h-3 animate-spin" /> : <ExternalLink className="w-3 h-3" />}
                            Manage Subscription
                        </button>
                        
                        {sub.subscription_tier !== 'free' && (
                            <button
                                onClick={() => setRefundModalOpen(true)}
                                className="flex items-center gap-2 px-3 py-1.5 border border-warning/20 text-warning bg-warning/5 rounded-[var(--fds-radius)] text-xs font-medium hover:bg-warning/10 transition-colors"
                            >
                                <HelpCircle className="w-3 h-3" />
                                Request Refund
                            </button>
                        )}
                    </div>
                </div>

                {sub.cancel_at_period_end && sub.subscription_tier !== 'free' && (sub.member_count > 1 || sub.pending_invites > 0 || sub.extra_seats > 0) && (
                    <div role="alert" className="flex items-start gap-3 p-3 rounded-[var(--fds-radius)] bg-danger/10 border border-danger/20 text-sm text-danger">
                        <AlertTriangle className="w-4 h-4 mt-0.5 shrink-0" />
                        <span>
                            Your plan is set to end{sub.current_period_end ? ` on ${new Date(sub.current_period_end).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' })}` : ''}.
                            {' '}On that date {sub.member_count - 1 > 0 ? `${sub.member_count - 1} team member${sub.member_count - 1 === 1 ? '' : 's'} will be removed` : 'your team access ends'}
                            {sub.pending_invites > 0 ? ` and ${sub.pending_invites} pending invite${sub.pending_invites === 1 ? '' : 's'} cancelled` : ''}.
                            {sub.extra_seats > 0 ? ` Your ${sub.extra_seats} extra seat${sub.extra_seats === 1 ? '' : 's'} will be removed too.` : ''}
                            {' '}Resume your plan in Manage Subscription to keep them.
                        </span>
                    </div>
                )}

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 pt-3 border-t border-line">
                    <div className="flex items-center gap-3 p-3 bg-panel rounded-[var(--fds-radius)] border border-line">
                        <Users className="w-5 h-5 text-fg-2" />
                        <div>
                            <p className="text-sm font-medium text-fg">{sub.member_count + sub.pending_invites} / {sub.max_members}</p>
                            <p className="text-xs text-fg-2">Team Seats Used{sub.pending_invites > 0 ? ' (incl. pending invites)' : ''}</p>
                        </div>
                    </div>
                    <div className="flex items-center gap-3 p-3 bg-panel rounded-[var(--fds-radius)] border border-line">
                        <CreditCard className="w-5 h-5 text-fg-2" />
                        <div>
                            <p className="text-sm font-medium text-fg">{tierDisplayName(sub.subscription_tier)}</p>
                            <p className="text-xs text-fg-2">Billing Tier</p>
                        </div>
                    </div>
                </div>
            </div>

            {/* Available Tiers */}
            <div className="space-y-3">
                <p className="text-xs font-medium text-fg-2 uppercase tracking-wider">Available Plans</p>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4 pt-2">
                    {TIERS.map((tier) => {
                        const isCurrent = sub.subscription_tier.toLowerCase() === tier.id;
                        const isComingSoon = !tier.active;
                        const isRecommended = tier.active && !isCurrent;

                        return (
                            <div
                                key={tier.id}
                                className={`relative flex flex-col p-5 rounded-xl border transition-all ${
                                    isCurrent
                                        ? 'bg-panel border-accent/50 ring-1 ring-accent/25'
                                        : isRecommended
                                            ? 'bg-panel border-2 border-accent'
                                            : 'bg-raised border-line'
                                } ${isComingSoon ? 'opacity-70' : ''}`}
                            >
                                {/* Badge — opaque, raised above the card border */}
                                {isCurrent ? (
                                    <div className="absolute -top-2.5 left-4 z-20 flex items-center gap-1 px-2.5 py-0.5 bg-menu border border-accent/50 rounded-full shadow-sm">
                                        <Sparkles className="w-3 h-3 text-accent" />
                                        <span className="text-[10px] font-semibold uppercase tracking-wider text-accent">Current</span>
                                    </div>
                                ) : isRecommended ? (
                                    <div className="absolute -top-2.5 left-4 z-20 flex items-center gap-1 px-2.5 py-0.5 bg-accent border border-accent rounded-full shadow-sm">
                                        <Sparkles className="w-3 h-3 text-on-accent" />
                                        <span className="text-[10px] font-semibold uppercase tracking-wider text-on-accent">Recommended</span>
                                    </div>
                                ) : (
                                    <div className="absolute -top-2.5 left-4 z-20 flex items-center gap-1 px-2.5 py-0.5 bg-menu border border-line rounded-full shadow-sm">
                                        <Clock className="w-3 h-3 text-fg-3" />
                                        <span className="text-[10px] font-semibold uppercase tracking-wider text-fg-3">Coming soon</span>
                                    </div>
                                )}

                                <p className="text-[10px] font-semibold uppercase tracking-[0.12em] text-fg-3 mt-2 mb-2">{tier.name}</p>
                                <p className="text-2xl font-bold text-fg">
                                    {priceFor(prices, tier.id, tier.price)}
                                    <span className="text-sm font-normal text-fg-2">{periodFor(prices, tier.id, tier.period)}</span>
                                </p>
                                <p className="text-xs text-fg-2 leading-relaxed mt-2 min-h-[2rem]">{tier.blurb}</p>

                                <div className="border-t border-line my-4" />

                                <ul className="space-y-2 flex-1">
                                    {tier.features.map((f, i) => (
                                        <li key={i} className="flex items-start gap-2 text-xs text-fg leading-snug">
                                            <CheckCircle2 className={`w-3.5 h-3.5 mt-0.5 shrink-0 ${isComingSoon ? 'text-fg-3' : 'text-accent'}`} />
                                            {f}
                                        </li>
                                    ))}
                                    {tier.inherits && (
                                        <li className="flex items-start gap-2 pt-2.5 mt-1 border-t border-line text-[11px] italic text-fg-2">
                                            <Sparkles className="w-3 h-3 mt-0.5 shrink-0 text-fg-3" />
                                            {tier.inherits}
                                        </li>
                                    )}
                                </ul>

                                {isCurrent ? (
                                    <div className="w-full mt-5 px-4 py-2 bg-panel border border-line rounded-[var(--fds-radius)] text-sm font-medium text-fg-2 text-center">
                                        Your current plan
                                    </div>
                                ) : isComingSoon ? (
                                    <div className="w-full mt-5 px-4 py-2 bg-panel border border-line rounded-[var(--fds-radius)] text-sm font-medium text-fg-3 text-center cursor-not-allowed">
                                        Coming soon
                                    </div>
                                ) : (
                                    <button
                                        onClick={() => {
                                            setCheckoutPriceId(tier.id);   // E-04: send the tier key, never a Stripe price id
                                            setCheckoutModalOpen(true);
                                        }}
                                        className="w-full mt-5 px-4 py-2 fd-btn-primary rounded-[var(--fds-radius)] text-sm font-semibold transition-opacity"
                                    >
                                        Upgrade to {tier.name}
                                    </button>
                                )}
                            </div>
                        );
                    })}
                </div>
            </div>

            {/* Modals */}
            {checkoutPriceId && sub.orgId && (
                <SubscriptionCheckoutModal
                    isOpen={checkoutModalOpen}
                    onClose={() => setCheckoutModalOpen(false)}
                    priceId={checkoutPriceId}
                    orgId={sub.orgId}
                />
            )}

            {sub.orgId && (
                <RefundRequestModal
                    isOpen={refundModalOpen}
                    onClose={() => setRefundModalOpen(false)}
                    userProfile={{
                        email: sub.user_email,
                        full_name: sub.user_full_name,
                        org_id: sub.orgId,
                        tier: sub.subscription_tier
                    }}
                />
            )}
        </div>
    );
};
